/* eslint-disable max-lines-per-function */
import React from "react";
import PopupState, { bindHover, bindMenu, bindTrigger } from "material-ui-popup-state";
import { Delete, Edit, Home, MailOutline, MoreVert } from "@material-ui/icons";
import { Menu, MenuItem, IconButton, Tooltip } from "@material-ui/core";
import HoverMenu from "material-ui-popup-state/HoverMenu";
import { PopupState as PopupStateType } from "material-ui-popup-state/core";
import { useMutation } from "@apollo/client";
import { useWindowSize } from "pages/eligibility/components/form/form-layout";

import {
  UserProfile_userProfile_addresses as Address,
  DeleteUserProfileAddress,
  SetAsUserProfileCurrentAddress,
  SetAsUserProfileMailingAddress,
  UserProfile_userProfile as UserProfile,
} from "types/gql-types";
import { deleteUserProfileAddressMutation, setAsUserProfileCurrentAddressMutation, setAsUserProfileMailingAddressMutation } from "pages/clients/settings/queries";
import { useSnackbar } from "components/haven-design/snackbar";
import { ProfileAddressesEditModal } from "./add-edit";

type AddressItemActionsType = {
  userProfile:UserProfile | null;
  address:Address;
  disabled?:boolean;
};

type AddressItemActionsMenuType = {
  editAddress: () => void;
  deleteAddress: () => void;
  setCurrentAddress: () => void;
  setMailingAddress: () => void;
  popupState: PopupStateType;
} & AddressItemActionsType;

const AddressItemActionsMenu: React.FC<AddressItemActionsMenuType> =
({
  popupState,
  editAddress,
  deleteAddress,
  address,
  userProfile,
  setMailingAddress,
  setCurrentAddress,
}) => {
  const isMailingAddress = address.id === userProfile?.mailingAddress?.id;
  const isCurrentAddress = address.id === userProfile?.currentAddress?.id;

  const deleteMenuItem = <MenuItem
    onClick={() => {
      deleteAddress();
      popupState.close();
    }}
    className="color-hc-error"
    disabled={isMailingAddress || isCurrentAddress}
  >
    <Delete />&nbsp; Delete
  </MenuItem>;

  return <>
    <MenuItem onClick={() => {
      editAddress();
      popupState.close();
    }}
    >
      <Edit />&nbsp; Edit Address
    </MenuItem>
    { !isMailingAddress &&
  <MenuItem onClick={() => {
    setMailingAddress();
    popupState.close();
  }}>
    <MailOutline />&nbsp; Set as Mailing Address
  </MenuItem>}
    { !isCurrentAddress &&
  <MenuItem onClick={() => {
    setCurrentAddress();
    popupState.close();
  }}>
    <Home />&nbsp; Set as Current Address
  </MenuItem>}
    {isMailingAddress || isCurrentAddress ? (
      <Tooltip title="Cannot delete current or mailing address.">
        <span>{deleteMenuItem}</span>
      </Tooltip>
    ) : (
      deleteMenuItem
    )}
  </>;
};

const AddressItemActions: React.FC<AddressItemActionsType> = (props) => {
  const { address, disabled } = props;
  const [isMobile, setIsMobile] = React.useState(window.outerWidth <= 695);
  const [isOpen, setIsOpen] = React.useState(false);

  const { openSnackbar } = useSnackbar();

  const [setAsMailingMutation, { loading: setAsMailingLoading }] = useMutation<SetAsUserProfileMailingAddress>(setAsUserProfileMailingAddressMutation, { refetchQueries: ["UserProfile"] });
  const [setAsCurrentMutation, { loading: setAsCurrentLoading }] = useMutation<SetAsUserProfileCurrentAddress>(setAsUserProfileCurrentAddressMutation, { refetchQueries: ["UserProfile"] });
  const [deleteAddressMutation, { loading: deleteAddressLoading }] = useMutation<DeleteUserProfileAddress>(deleteUserProfileAddressMutation, { refetchQueries: ["UserProfile"] });

  const size = useWindowSize();
  const disableMenu = disabled ||
    setAsMailingLoading ||
    setAsCurrentLoading ||
    deleteAddressLoading;

  const actionsMenu = (popupState:PopupStateType) => (
    <AddressItemActionsMenu
      editAddress={() => setIsOpen(true)}
      deleteAddress={
        () => deleteAddressMutation({ variables: { id: address.id } }).then(() => openSnackbar(
          { message: "Address successfully removed.", status: "success" },
        ))
      }
      setMailingAddress={
        () => setAsMailingMutation({ variables: { id: address.id } }).then(() => openSnackbar(
          { message: "Mailing Address successfully updated.", status: "success" },
        ))
      }
      setCurrentAddress={
        () => setAsCurrentMutation({ variables: { id: address.id } }).then(() => openSnackbar(
          { message: "Current Address successfully updated.", status: "success" },
        ))
      }
      popupState={popupState}
      {...props} />
  );

  React.useEffect(() => setIsMobile(size.width < 960), [size]);
  return (
    <PopupState variant="popover" popupId={`address-menu-${address.id}`}>
      {(popupState) => (isMobile ? (<>
        <ProfileAddressesEditModal
          isOpen={isOpen}
          setIsOpen={setIsOpen}
          address={address} />
        <IconButton color="primary" size="small"
          disabled={disableMenu}
          {...bindTrigger(popupState)}
        >
          <MoreVert />
        </IconButton>
        <Menu {...bindMenu(popupState)}>
          {actionsMenu(popupState)}
        </Menu>
      </>) : (
        <>
          <ProfileAddressesEditModal
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            address={address} />
          <IconButton color="primary" size="small"
            {...bindHover(popupState)}
            disabled={disableMenu}
          >
            <MoreVert />
          </IconButton>
          <HoverMenu {...bindMenu(popupState)}>
            {actionsMenu(popupState)}
          </HoverMenu>
        </>)
      )}
    </PopupState>);
};

export default AddressItemActions;
